<template>
  <div :class="getClassName">
    <configField
      v-if="isVisible"
      :availableFieldList="field.AvailableFieldList"
      :category="category"
      :disabled="getDisabled"
      :field="field"
      :headers="getHeaders"
      :helpContent="
        getFieldValue({
          templateKey: 'HelpContent',
          fieldMapKey: 'helpContent',
        })
      "
      :hideLabel="hideLabel"
      :hideTooltip="hideTooltip"
      :id="field.FieldId"
      :icon="getFieldValue({ templateKey: 'Icon', fieldMapKey: 'icon' })"
      :isInModal="isInModal"
      :isTemplate="isTemplate"
      :label="getLabel"
      :layout="layout"
      :message="
        getFieldValue({ templateKey: 'Message', fieldMapKey: 'message' })
      "
      :options="getFieldMapValue('options') || field.Options"
      :parentField="parentField"
      :placeholder="getPlaceholder"
      :prefix="getFieldValue({ templateKey: 'Prefix', fieldMapKey: 'prefix' })"
      ref="input"
      :ruleId="id"
      :rules="getRules"
      :tooltip="
        getFieldValue({ templateKey: 'Tooltip', fieldMapKey: 'tooltip' })
      "
      :type="getFieldMapValue('type') || field.Type"
      :value="formattedValue"
      :warning="getWarningMessage"
      :width="
        getFieldValue({ templateKey: 'Width', fieldMapKey: 'width' }) || width
      "
      @setConfigValue="setConfigValue"
      @unsetConfigValue="unsetConfigValue"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import fieldMap from "./field-map.json";
import lossImpactConfigs from "./loss-impact-configs.json";

import configField from "./config-field";

export default {
  components: {
    configField,
  },
  name: "config-field-wrapper",
  data() {
    return {
      selectedAnswer: "",
    };
  },
  props: {
    category: {
      type: String,
      required: true,
    },
    emitSetConfig: {
      type: Boolean,
      default: false,
    },
    field: {
      type: Object,
      required: true,
    },
    headers: {
      type: Array,
      default() {
        return [];
      }
    },
    hideLabel: {
      type: Boolean,
    },
    hideTooltip: {
      type: Boolean,
    },
    icon: {
      type: String,
    },
    id: {
      type: String,
    },
    index: {
      type: Number,
      required: false,
    },
    isInModal: {
      type: Boolean,
      default: false,
    },
    isTemplate: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
    },
    layout: {
      type: Array,
    },
    parentField: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    prefix: {
      type: String,
    },
    tooltip: {
      type: String,
    },
    value: {
      type: [String, Object, Number, Boolean, Array],
      default: undefined,
    },
    visible: {
      type: [Boolean, String],
      default: undefined,
    },
    warning: {
      type: String,
    },
    width: {
      type: String,
    },
  },
  computed: {
    getClassName() {
      let classNames = "";
      if (this.field && this.field.ClassNames) {
        classNames = this.field.ClassNames;
      }
      if (!classNames.includes("indentedChild")) {
        if (this.field.depth > 0) {
          classNames = "indentedChild";
        }
      }
      return classNames;
    },
    getDisabled() {
      let isDisabled = false;
      if (this.field && this.field.Disabled !== undefined) {
        isDisabled = this.field.Disabled;
      } else if (this.getFieldMapValue("disabled") !== undefined) {
        isDisabled = this.getFieldMapValue("disabled");
      }
      return isDisabled;
    },
    getFullConfigPath() {
      return this.parentField
        ? `${this.category}.${this.parentField}.${this.field.FieldId}`
        : `${this.category}.${this.field.FieldId}`;
    },
    getLabel() {
      let returnValue = "";
      if (!this.hideLabel || this.field.Type === "checkbox") {
        returnValue = this.getFieldValue({
          templateKey: "Label",
          fieldMapKey: "label",
        });
      }
      return returnValue;
    },
    getHeaders() {
      let returnValue = this.getFieldValue({
        templateKey: "Headers",
        fieldMapKey: "headers"
      });
      return returnValue || [];

    },
    getPlaceholder() {
      let returnValue = this.getFieldValue({
        templateKey: "Placeholder",
        fieldMapKey: "placeholder",
        returnUndefined: true,
      });
      if (returnValue === undefined) {
        if (this.field.Type === "dropdown") {
          returnValue = "Select...";
        } else {
          returnValue = this.getFieldValue({
            templateKey: "Label",
            fieldMapKey: "label",
          });
        }
      }
      return returnValue;
    },
    getRules() {
      return this.getFieldMapValue("rules") || this.field.Rules;
    },
    getWarningMessage() {
      let warning = this.warning;

      // check for if loss impact warning should be displayed
      if (
        lossImpactConfigs.includes(this.field.FieldId) &&
        this.hasUpdatedValue
      ) {
        warning = "This change could result in a loss impact.";
      }
      return warning;
    },
    formattedValue() {
      let formattedValue = this.value;
      if (this.field.DefaultValueRef !== undefined) {
        const breakpoint = /\s*&&\s*/;
        const fieldRefs = this.field.DefaultValueRef.split(breakpoint);
        let returnValue;
        for (let fieldRef of fieldRefs) {
          const keys = fieldRef.split(".");
          const sourceValue =
            this.mergedConfig[this.id] &&
            this.mergedConfig[this.id][keys[0]] &&
            this.mergedConfig[this.id][keys[0]][keys[1]];
          if (sourceValue !== undefined) {
            if (returnValue === undefined) {
              returnValue = sourceValue;
            } else {
              returnValue = returnValue && sourceValue;
            }
          }
        }
        if (returnValue !== undefined) {
          return returnValue;
        }
      }
      if (formattedValue === undefined && !this.isBulkUpdate) {
        if (this.field.DefaultValue !== undefined) {
          formattedValue = this.field.DefaultValue;
        } else if (this.getFieldMapValue("defaultValue") !== undefined) {
          formattedValue = this.getFieldMapValue("defaultValue");
        }
      }
      return formattedValue;
    },
    hasUpdatedValue() {
      const updatedValue = this.parentField
        ? this.updatedConfigValues[this.id] &&
          this.updatedConfigValues[this.id][this.category] &&
          this.updatedConfigValues[this.id][this.category][this.parentField] &&
          this.updatedConfigValues[this.id][this.category][this.parentField][
            this.field.FieldId
          ]
        : this.updatedConfigValues[this.id] &&
          this.updatedConfigValues[this.id][this.category] &&
          this.updatedConfigValues[this.id][this.category][this.field.FieldId];
      return updatedValue !== undefined;
    },
    isBulkUpdate() {
      return this.id === "bulkEdit" || this.id === "bulkDelete";
    },
    isVisible() {
      const visible =
        this.visible !== undefined
          ? this.visible
          : this.configVisibility[this.id] &&
            this.configVisibility[this.id][this.category] &&
            this.configVisibility[this.id][this.category][this.field.FieldId];

      return (
        this.field.FieldId &&
        visible &&
        !(this.isTemplate && this.getFieldMapValue("personalDetail"))
      );
    },
    needsValidation() {
      return !!this.validationRequests[this.getFullConfigPath];
    },
    validationConnectedFields() {
      // if the validation rule is of type "expression", there can be other fields that could be involved with
      // the field validation

      if (!this.getRules) return [];

      const expressionRules = this.getRules.filter(
        (rule) => rule.Type === "expression"
      );
      if (!expressionRules.length) return [];

      let fields = expressionRules
        .map((rule) => {
          const fields = rule.Value.match(/ConfigField\.[\w.]+/g);

          return fields.map((field) => {
            const parts = field.split(".");
            return parts.slice(1).join(".");
          });
        })
        .flat();

      // using Set to get only unique values
      fields = [...new Set(fields)];

      if (!fields.length) return [];

      // filter out self
      fields = fields.filter((field) => field !== this.getFullConfigPath);

      return fields;
    },
    ...mapGetters({
      configVisibility: "getConfigVisibility",
      debugMode: "getDebugMode",
      mergedConfig: "getMergedConfig",
      updatedConfigValues: "getUpdatedConfigValues",
      validationRequests: "getValidationRequests",
    }),
  },
  methods: {
    getFieldMapValue(value) {
      return (
        fieldMap[this.category] &&
        fieldMap[this.category][this.field.FieldId] &&
        fieldMap[this.category][this.field.FieldId][value]
      );
    },
    getFieldValue({ templateKey, fieldMapKey, returnUndefined }) {
      let returnValue;
      if (!returnUndefined) {
        returnValue = "";
      }
      if (this.field && this.field[templateKey] !== undefined) {
        returnValue = this.field[templateKey];
      } else if (this.getFieldMapValue(fieldMapKey) !== undefined) {
        returnValue = this.getFieldMapValue(fieldMapKey);
      }
      return returnValue;
    },
    setConfigValue(args) {
      if (this.emitSetConfig) {
        this.$emit("setConfigValue", { index: this.index, ...args });
      } else {
        this.$store.dispatch("setConfigValue", {
          ClientInfoRuleId: this.id,
          ...args,
        });
      }
    },
    unsetConfigValue(args) {
      if (this.emitSetConfig) {
        this.$emit("unsetConfigValue", { index: this.index, ...args });
      } else {
        this.$store.dispatch("unsetConfigValue", {
          ClientInfoRuleId: this.id,
          ...args,
        });
      }
    },
  },
  watch: {
    formattedValue() {
      this.validationConnectedFields.forEach((field) => {
        this.$store.dispatch("validateConfig", field);
      });
    },
    needsValidation(value) {
      if (value) {
        if (this.$refs.input.validate) {
          this.$refs.input.validate();
        }
        this.$store.dispatch("clearValidateConfig", this.getFullConfigPath);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.indentedChild {
  margin-left: 32px;
}

.objectElement .indentedChild {
  margin-left: 0px;
}
.config-field {
  &:not(:has(.config-field)) {
    // only apply margin when configs aren't inside other configs
    margin-bottom: $qvo-spacing-24;
  }
  &:has(.dropdown-table) {
    // dropdown tables are special as they contain other configs, but still need that margin at the bottom
    margin-bottom: $qvo-spacing-24;
  }
  &:has(.content h6) {
    // content configs with headers should have a smaller margin
    margin-bottom: $qvo-spacing-8;
  }
  &::v-deep {
    // this applies to content-groups that are inside of object configs types
    .show-highlight .content-group {
      padding: 0;
      margin-bottom: 0;
    }
    .show-border .content-group {
      padding: $qvo-spacing-16 $qvo-spacing-16;
      margin-bottom: $qvo-spacing-24;
    }
    .show-border .content-field {
      margin-bottom: 9px;
    }
  }
}

.v-tooltip__content {
  color: $qvo-color-grey-000;
}
</style>